import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { LogOut, CalendarDays, Package, Ticket, Mail, Users, LayoutDashboard } from "lucide-react";
import { fetcher, TOKEN_KEY } from "@/lib/api";
import { Overview } from "@/components/admin/Overview";
import { MeetingsTab } from "@/components/admin/MeetingsTab";
import { OrdersTab, PliTab, MessagesTab } from "@/components/admin/RecordTabs";
import { TeamTab } from "@/components/admin/TeamTab";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard, C: Overview },
  { id: "meetings", label: "Meetings", icon: CalendarDays, C: MeetingsTab },
  { id: "orders", label: "Sample Orders", icon: Package, C: OrdersTab },
  { id: "pli", label: "PLI Registrations", icon: Ticket, C: PliTab },
  { id: "messages", label: "Messages", icon: Mail, C: MessagesTab },
  { id: "team", label: "Team", icon: Users, C: TeamTab, adminOnly: true },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const hasToken = !!localStorage.getItem(TOKEN_KEY);
  const { data: me, error } = useSWR(hasToken ? "/auth/me" : null, fetcher);

  useEffect(() => {
    if (!hasToken || error) {
      localStorage.removeItem(TOKEN_KEY);
      nav("/admin/login", { replace: true });
    }
  }, [hasToken, error, nav]);

  if (!me) return <div className="min-h-screen flex items-center justify-center font-editorial text-3xl text-[#1D3A2F]">Loading…</div>;

  const tabs = TABS.filter((t) => !t.adminOnly || me.role === "admin");
  const Active = (tabs.find((t) => t.id === tab) || tabs[0]).C;
  const logout = () => { localStorage.removeItem(TOKEN_KEY); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#F7F4EE]" data-testid="admin-dashboard">
      <header className="bg-[#1D3A2F] text-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-editorial text-2xl">Fresio<sup className="text-xs">®</sup></span>
            <span className="text-[10px] tracking-[0.24em] uppercase text-[#E6D5A4]">Admin</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span data-testid="admin-user-email" className="hidden sm:inline text-[#FDFBF7]/70">{me.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 h-9 px-4 rounded-full border border-[#FDFBF7]/20 hover:border-[#C9A961] transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <nav className="flex gap-2 overflow-x-auto no-scrollbar mb-8">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`shrink-0 inline-flex items-center gap-2 h-11 px-5 rounded-full text-sm border transition-colors ${tab === id ? "bg-[#1D3A2F] text-[#F4E4BD] border-[#1D3A2F]" : "bg-white border-[#D7D4CF] text-[#1D3A2F] hover:border-[#C9A961]"}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>
        <Active me={me} goTo={setTab} />
      </div>
    </div>
  );
}
